import { useEffect, useState } from "react";
import { Trash2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { OPS } from "@/constants/testIds";
import { CO_TIMEOUT_MS, coMessage } from "./coUtils";

const P = OPS.co.panel;
const REASON_MIN = 5;

/** Message 36: «حذف السجل» — masters only (branch_ops_full_control / super admin).
 *  A soft delete with a required reason; the photo is kept for a restore.
 *  Controlled (`open` + `onOpenChange` given): no button of its own — the
 *  caller's control opens the dialog (the row's trash icon in «إدخالاتي»). */
export function RecordDelete({ rec, scope, onDeleted, open: openProp, onOpenChange }) {
    const { t, lang } = useI18n();
    const [openOwn, setOpenOwn] = useState(false);
    const [reason, setReason] = useState("");
    const [busy, setBusy] = useState(false);
    const controlled = openProp !== undefined;
    const open = controlled ? !!openProp : openOwn;
    const setOpen = (o) => { if (controlled) onOpenChange && onOpenChange(o); else setOpenOwn(o); };
    // a fresh reason every time the dialog opens, whoever opened it
    useEffect(() => { if (open) setReason(""); }, [open]);
    if (!scope?.caps?.is_master) return null;
    const valid = reason.trim().length >= REASON_MIN;

    const confirm = async () => {
        setBusy(true);
        try {
            await api.delete(`/co/records/${rec.id}`, { data: { reason: reason.trim() }, timeout: CO_TIMEOUT_MS });
            toast.success(t("co_deleted"));
            setOpen(false);
            onDeleted && onDeleted();
        } catch (e) {
            toast.error(coMessage(e, lang) || t("co_offline_error"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            {!controlled ? (
                <button type="button" data-testid={P.del} onClick={() => { setReason(""); setOpen(true); }}
                        className="flex h-11 w-full items-center justify-center gap-2 rounded-full border border-[color:var(--danger-soft-fg)] text-sm font-bold text-[color:var(--danger-soft-fg)] transition-[background-color] duration-150 hover:bg-[color:var(--danger-soft)]">
                    <Trash2 className="h-4 w-4" />{t("co_rec_delete")}
                </button>
            ) : null}
            <Dialog open={open} onOpenChange={(o) => { if (!busy) setOpen(o); }}>
                <DialogContent data-testid={P.delDialog} className="bg-[color:var(--bg-surface)] sm:max-w-md">
                    <DialogHeader className="text-start sm:text-start">
                        <DialogTitle>{t("co_rec_delete_title")} <span dir="ltr">#{rec.invoice_no}</span></DialogTitle>
                        <DialogDescription>{t("co_rec_delete_body")}</DialogDescription>
                    </DialogHeader>
                    <label className="space-y-1.5 text-sm font-semibold text-[color:var(--ink)]">
                        <span>{t("co_rec_delete_reason")}</span>
                        <Textarea data-testid={P.delReason} value={reason} maxLength={500} rows={3}
                                  onChange={(e) => setReason(e.target.value)} />
                        <span className="block text-[11px] font-normal text-[color:var(--ink-faint)]">{t("co_rec_delete_reason_hint")}</span>
                    </label>
                    <DialogFooter className="gap-2 sm:gap-2">
                        <Button type="button" variant="outline" data-testid={P.delCancel} disabled={busy} onClick={() => setOpen(false)}>
                            {t("co_rec_delete_cancel")}
                        </Button>
                        <Button type="button" variant="destructive" data-testid={P.delConfirm} disabled={!valid || busy} onClick={confirm}>
                            {t("co_rec_delete_confirm")}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
